import React, { useState, useRef, useEffect, createContext, useContext } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence, useScroll, useTransform, useSpring, useMotionValue, animate } from 'framer-motion';
import { FaMobileAlt, FaDesktop, FaLayerGroup, FaChevronDown, FaChevronUp, FaTimes, FaDownload, FaCalculator, FaHeadset } from 'react-icons/fa';

import girisGif from '../photo/photo2/giris.gif';
import tumPlatformlarda from '../photo/photo2/tum platformlarda.png';
import mobilDashboard from '../photo/photo2/mobil dashboard.png';
import mobilRapor from '../photo/photo2/mobil rapor goruntuleme.png';
import mobilBildirim from '../photo/photo2/mobil bildirim.png';
import webBildirim from '../photo/photo2/web bildirim.png';
import mobilAktivite1 from '../photo/photo2/mobil aktivite 1.png';
import mobilAktivite2 from '../photo/photo2/mobil aktivite 2.png';
import mobilHedef1 from '../photo/photo2/mobil hedef 1.png';
import mobilHedef2 from '../photo/photo2/mobil hedef 2.png';
import webDashboard from '../photo/photo2/web dashboard.png';
import webRapor from '../photo/photo2/web rapor goruntuleme.png';
import webAktivite1 from '../photo/photo2/web aktivite 1.png';
import webAktivite2 from '../photo/photo2/web aktivite 2.png';
import webHedef1 from '../photo/photo2/web hedef1.png';
import webHedef2 from '../photo/photo2/web hedef 2.png';
import webEFatura1 from '../photo/photo2/web e fatura sureci 1.png';
import webEFatura2 from '../photo/photo2/web e fatura sureci 2.png';
import { Download } from '../components/Download';
import { PriceCalculator } from '../components/PriceCalculator';
import { ContactUs } from '../components/ContactUs';

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(8px); }
`;

const INTRO_GRAY = '#e8e8ec';
const CONTENT_GRAY = '#e8e8ec';

const LightboxContext = createContext(null);

const LightboxOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  z-index: 10001;
  background: rgba(8, 8, 12, 0.88);
  backdrop-filter: blur(12px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  cursor: zoom-out;
`;

const LightboxPanel = styled(motion.div)`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  max-width: 96vw;
  max-height: 92vh;
  cursor: default;
`;

const LightboxImage = styled.img`
  max-width: 96vw;
  max-height: 85vh;
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 12px;
  box-shadow: 0 28px 80px rgba(0, 0, 0, 0.45);
  user-select: none;
`;

const LightboxCaption = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.95rem;
  font-weight: 500;
  text-align: center;
`;

const LightboxClose = styled.button`
  position: fixed;
  top: 1.25rem;
  right: 1.25rem;
  z-index: 10002;
  width: 44px;
  height: 44px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.16);
  }
`;

const ImageLightbox = ({ image, onClose }) => {
  useEffect(() => {
    if (!image) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [image, onClose]);

  return (
    <AnimatePresence>
      {image && (
        <LightboxOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
        >
          <LightboxClose type="button" aria-label="Kapat" onClick={onClose}>
            <FaTimes size={16} />
          </LightboxClose>
          <LightboxPanel
            initial={{ opacity: 0, scale: 0.92, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            onClick={(event) => event.stopPropagation()}
          >
            <LightboxImage src={image.src} alt={image.alt} />
            <LightboxCaption>{image.alt}</LightboxCaption>
          </LightboxPanel>
        </LightboxOverlay>
      )}
    </AnimatePresence>
  );
};

const PageWrapper = styled.div`
  min-height: 100vh;
  background: ${CONTENT_GRAY};
`;

/* ─── Cinematic Intro ─── */
const IntroSection = styled.section`
  height: 160vh;
  position: relative;

  @media (max-width: 768px) {
    height: 140vh;
  }
`;

const IntroSticky = styled.div`
  position: sticky;
  top: 0;
  height: 100vh;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  perspective: 2200px;
  transform-style: preserve-3d;
  cursor: grab;
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;

  &:active {
    cursor: grabbing;
  }

  @media (max-width: 768px) {
    touch-action: pan-y;
    cursor: default;
    overflow: visible;

    &:active {
      cursor: default;
    }
  }
`;

const IntroBg = styled(motion.div)`
  position: absolute;
  inset: 0;
  background: ${INTRO_GRAY};
`;

const HeroStage = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  perspective: 2200px;
  pointer-events: none;
`;

const HeroLayer = styled(motion.div)`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  transform-style: preserve-3d;
  will-change: transform, opacity;
  backface-visibility: hidden;
`;

const HeroPhoneImg = styled.img`
  width: min(96vw, 1400px);
  max-height: 98vh;
  height: auto;
  object-fit: contain;
  display: block;
  background: transparent;
  border: none;
  box-shadow: none;
  outline: none;

  @media (max-width: 768px) {
    width: min(92vw, 960px);
    max-height: 72vh;
  }
`;

const HeroPlatformImg = styled.img`
  width: min(96vw, 1720px);
  max-height: 96vh;
  height: auto;
  object-fit: contain;
  display: block;

  @media (max-width: 768px) {
    width: min(92vw, 960px);
    max-height: 72vh;
  }
`;

const ScrollHint = styled(motion.div)`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: rgba(0, 0, 0, 0.42);
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  z-index: 6;
  pointer-events: none;
  text-align: center;
  max-width: 90vw;

  svg {
    animation: ${float} 2.5s ease-in-out infinite;
  }
`;

const IntroFadeBridge = styled(motion.div)`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 20vh;
  background: linear-gradient(180deg, transparent 0%, ${INTRO_GRAY} 100%);
  pointer-events: none;
  z-index: 4;
`;

/* ─── Content Area ─── */
const ContentArea = styled(motion.div)`
  position: relative;
  z-index: 5;
  margin-top: -8vh;
  background: ${CONTENT_GRAY};
  padding-bottom: 6rem;
`;

const StickyHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 100;
  padding: 1.25rem 2rem;
  background: rgba(232, 232, 236, 0.92);
  backdrop-filter: blur(24px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);

  @media (max-width: 768px) {
    padding: 0.75rem 0.75rem 0.85rem;
  }
`;

const HeaderTitle = styled.span`
  font-size: 1.1rem;
  font-weight: 700;
  color: #1a1a1e;
  letter-spacing: -0.01em;
  flex-shrink: 0;

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0 0.25rem;
  }
`;

const FilterTabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 4px;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  justify-content: flex-end;

  @media (max-width: 768px) {
    width: 100%;
    flex-wrap: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
    justify-content: flex-start;
    gap: 8px;
    padding: 6px;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    mask-image: linear-gradient(90deg, #000 92%, transparent);

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const FilterTab = styled(motion.button)`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 9px 14px;
  border-radius: 12px;
  border: none;
  background: ${props => props.$active ? 'linear-gradient(135deg, #3a3a42, #2a2a2e)' : 'transparent'};
  color: ${props => props.$active ? '#fff' : 'rgba(0, 0, 0, 0.45)'};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: ${props => props.$active ? '0 4px 16px rgba(0, 0, 0, 0.15)' : 'none'};
  transition: color 0.2s;
  white-space: nowrap;
  flex-shrink: 0;

  .label-short {
    display: none;
  }

  &:hover {
    color: #1a1a1e;
  }

  @media (max-width: 768px) {
    min-height: 44px;
    padding: 10px 14px;
    font-size: 12px;

    .label-full {
      display: none;
    }

    .label-short {
      display: inline;
    }
  }
`;

const HeaderInner = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }
`;

const ScrollFabGroup = styled.div`
  position: fixed;
  right: 1.1rem;
  bottom: 1.25rem;
  z-index: 9998;
  display: flex;
  flex-direction: column;
  gap: 10px;

  @media (max-width: 768px) {
    right: 0.85rem;
    bottom: 1rem;
  }
`;

const ScrollFab = styled(motion.button)`
  width: 46px;
  height: 46px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.94);
  color: #2a2a2e;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.14);
  backdrop-filter: blur(12px);

  &:hover {
    background: #fff;
  }

  @media (max-width: 768px) {
    width: 44px;
    height: 44px;
  }
`;

const ContentSection = styled.section`
  max-width: 1500px;
  margin: 0 auto;
  padding: 4rem 2rem 3rem;

  @media (max-width: 768px) {
    padding: 2rem 0.85rem 2.5rem;
  }
`;

const PanelSection = styled(motion.section)`
  scroll-margin-top: 6rem;
`;

const ShowcaseCard = styled(motion.article)`
  margin-bottom: 6rem;
  position: relative;
`;

const CardHeader = styled.div`
  margin-bottom: 2.5rem;
`;

const CardTag = styled.span`
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.4);
  margin-bottom: 0.75rem;
`;

const CardTitle = styled.h2`
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 800;
  letter-spacing: -0.03em;
  color: #1a1a1e;
  margin-bottom: 0.75rem;
  line-height: 1.1;
`;

const CardDescription = styled.p`
  font-size: 1.1rem;
  color: rgba(0, 0, 0, 0.5);
  max-width: 600px;
  line-height: 1.7;
`;

const ImageGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3rem;
`;

const CompareRow = styled.div`
  display: grid;
  grid-template-columns: clamp(400px, 48%, 520px) minmax(0, 1fr);
  grid-template-rows: auto auto;
  column-gap: clamp(1rem, 2vw, 1.75rem);
  row-gap: 1rem;
  align-items: start;
  width: 100%;
  --mobile-compare-width: 520px;
  --compare-media-height: clamp(380px, 50vh, 520px);

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    grid-template-rows: none;
    justify-items: center;
  }
`;

const CompareLabelSlot = styled(motion.div)`
  min-width: 0;

  ${props => props.$mobile && `
    grid-column: 1;
    grid-row: 1;
    max-width: var(--mobile-compare-width, 260px);
  `}

  ${props => props.$web && `
    grid-column: 2;
    grid-row: 1;
  `}

  @media (max-width: 900px) {
    grid-column: 1;
    grid-row: auto;
  }
`;

const CompareMediaSlot = styled(motion.div)`
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: ${props => (props.$mobile ? 'flex-start' : 'stretch')};
  width: 100%;

  ${props => props.$mobile && `
    grid-column: 1;
    grid-row: 2;
    max-width: var(--mobile-compare-width, 520px);
    height: var(--compare-media-height);
    min-height: var(--compare-media-height);
    align-items: stretch;
  `}

  ${props => props.$web && `
    grid-column: 2;
    grid-row: 2;
    min-height: var(--compare-media-height);
    height: var(--compare-media-height);
    align-items: stretch;
  `}

  @media (max-width: 900px) {
    grid-column: 1;
    grid-row: auto;
    max-width: ${props => (props.$mobile ? 'min(var(--mobile-compare-width, 260px), 94vw)' : '100%')};

    ${props => props.$mobile && `
      padding-top: 0;
      min-height: 0;
    `}
  }
`;

const CompareCell = styled(motion.div)`
  min-width: 0;
  display: flex;
  flex-direction: column;

  ${props => props.$mobile && `
    flex: 0 0 clamp(200px, 24%, 260px);
    max-width: 260px;
    width: clamp(200px, 24%, 260px);
  `}

  ${props => props.$web && `
    flex: 1;
    width: 100%;
  `}
`;

const CompareMedia = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: ${props => (props.$mobile ? 'flex-start' : 'stretch')};
  width: 100%;

  ${props => props.$web && `
    min-height: clamp(380px, 50vh, 520px);
  `}
`;

const SingleImageRow = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const PlatformLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  min-height: 34px;
  margin-bottom: ${props => (props.$compact ? '0' : '1rem')};
  padding: 6px 14px;
  border-radius: 20px;
  background: ${props => props.$mobile
    ? 'rgba(220, 38, 38, 0.08)'
    : 'rgba(37, 99, 235, 0.08)'};
  border: 1px solid ${props => props.$mobile
    ? 'rgba(220, 38, 38, 0.2)'
    : 'rgba(37, 99, 235, 0.2)'};
  color: ${props => props.$mobile ? '#b91c1c' : '#1d4ed8'};
`;

const BrowserFrame = styled.div`
  border-radius: 16px;
  overflow: hidden;
  background: #fff;
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.08),
    0 20px 50px rgba(0, 0, 0, 0.1);
  transition: transform 0.4s ease, box-shadow 0.4s ease;
  width: 100%;
  display: flex;
  flex-direction: column;
  cursor: ${props => (props.$clickable ? 'zoom-in' : 'default')};

  ${props => props.$fillHeight ? `
    flex: 1;
    width: 100%;
    min-height: 0;
  ` : `
    height: 100%;
  `}

  &:hover {
    transform: translateY(-4px);
    box-shadow:
      0 0 0 1px rgba(0, 0, 0, 0.1),
      0 28px 60px rgba(0, 0, 0, 0.14);
  }

  .browser-bar {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    background: #f4f4f6;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }

  .dot {
    width: 11px;
    height: 11px;
    border-radius: 50%;
  }

  .dot.red { background: #ff5f57; }
  .dot.yellow { background: #febc2e; }
  .dot.green { background: #28c840; }

  .url-bar {
    flex: 1;
    margin-left: 10px;
    padding: 7px 14px;
    background: #fff;
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.08);
    font-size: 12px;
    color: rgba(0, 0, 0, 0.45);
    font-family: 'SF Mono', 'Fira Code', monospace;
  }

  img {
    flex: 1;
    width: 100%;
    min-height: 0;
    object-fit: contain;
    object-position: top center;
    display: block;
    background: #fff;
  }
`;

const MobilePhoneSmall = styled.div`
  width: 100%;
  max-width: var(--mobile-compare-width, 520px);
  line-height: 0;
  transition: transform 0.4s ease;
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: flex-start;
  cursor: ${props => (props.$clickable ? 'zoom-in' : 'default')};

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: top center;
    display: block;
    filter: drop-shadow(0 14px 36px rgba(0, 0, 0, 0.1));
    pointer-events: ${props => (props.$clickable ? 'none' : 'auto')};
  }

  ${props => props.$compact && `
    flex: none;
    max-width: 260px;

    img {
      height: auto;
    }
  `}

  &:hover {
    transform: ${props => (props.$clickable ? 'translateY(-2px)' : 'translateY(-4px)')};
  }
`;

const WebOnlyGrid = styled.div`
  display: grid;
  gap: 2rem;
  grid-template-columns: ${props => props.$count === 1 ? '1fr' : 'repeat(2, 1fr)'};

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const sections = [
  {
    id: 'dashboard',
    tag: '01 — Dashboard',
    title: 'Dashboard Görünümü',
    description: 'KPI\'larınız, grafikleriniz ve önemli metrikleriniz hem mobil hem web ortamında modern bir arayüzle.',
    filter: 'all',
    images: [
      { src: mobilDashboard, platform: 'mobile', label: 'Mobil Dashboard' },
      { src: webDashboard, platform: 'web', label: 'Web Dashboard' },
    ],
  },
  {
    id: 'reports',
    tag: '02 — Raporlar',
    title: 'Rapor Görüntüleme',
    description: 'Satış, stok, finans ve operasyon raporlarınızı detaylı filtreleme ve görüntüleme seçenekleriyle inceleyin.',
    filter: 'all',
    images: [
      { src: mobilRapor, platform: 'mobile', label: 'Mobil Raporlar' },
      { src: webRapor, platform: 'web', label: 'Web Raporlar' },
    ],
  },
  {
    id: 'notifications',
    tag: '03 — Bildirimler',
    title: 'Anlık Bildirimler',
    description: 'Kritik iş olayları hakkında anında bildirim alın. Mobil push ve web panel üzerinden takip edin.',
    filter: 'all',
    images: [
      { src: mobilBildirim, platform: 'mobile', label: 'Mobil Bildirimler' },
      { src: webBildirim, platform: 'web', label: 'Web Bildirimler' },
    ],
  },
  {
    id: 'activity',
    tag: '04 — Aktivite',
    title: 'Aktivite Takibi',
    description: 'Ekip aktivitelerini, görevleri ve iş akışlarını gerçek zamanlı takip edin.',
    filter: 'all',
    images: [
      { src: mobilAktivite1, platform: 'mobile', label: 'Mobil Aktivite' },
      { src: mobilAktivite2, platform: 'mobile', label: 'Mobil Aktivite Detay' },
      { src: webAktivite1, platform: 'web', label: 'Web Aktivite' },
      { src: webAktivite2, platform: 'web', label: 'Web Aktivite Detay' },
    ],
  },
  {
    id: 'goals',
    tag: '05 — Hedefler',
    title: 'Hedef Yönetimi',
    description: 'Satış hedeflerinizi tanımlayın, ilerlemeyi izleyin ve performansı analiz edin.',
    filter: 'all',
    images: [
      { src: mobilHedef1, platform: 'mobile', label: 'Mobil Hedefler' },
      { src: mobilHedef2, platform: 'mobile', label: 'Mobil Hedef Detay' },
      { src: webHedef1, platform: 'web', label: 'Web Hedefler' },
      { src: webHedef2, platform: 'web', label: 'Web Hedef Detay' },
    ],
  },
  {
    id: 'efatura',
    tag: '06 — E-Fatura',
    title: 'E-Fatura Süreci',
    description: 'E-fatura işlemlerinizi web panelinden uçtan uca yönetin. Süreç takibi ve onay akışları tek ekranda.',
    filter: 'web',
    images: [
      { src: webEFatura1, platform: 'web', label: 'E-Fatura Süreci' },
      { src: webEFatura2, platform: 'web', label: 'E-Fatura Detay' },
    ],
  },
];

const filters = [
  { id: 'all', label: 'Tümü', shortLabel: 'Tümü', icon: FaLayerGroup },
  { id: 'mobile', label: 'Mobil', shortLabel: 'Mobil', icon: FaMobileAlt },
  { id: 'web', label: 'Web', shortLabel: 'Web', icon: FaDesktop },
  { id: 'download', label: 'Uygulama İndir', shortLabel: 'İndir', icon: FaDownload },
  { id: 'pricing', label: 'Fiyat Hesapla', shortLabel: 'Fiyat', icon: FaCalculator },
  { id: 'contact', label: 'Bize Ulaşın', shortLabel: 'Ulaş', icon: FaHeadset },
];

const GALLERY_PANELS = new Set(['all', 'mobile', 'web']);
const PANEL_SCROLL_IDS = new Set(['download', 'pricing', 'contact']);

const getComparePairs = (images) => {
  const mobile = images.filter(i => i.platform === 'mobile');
  const web = images.filter(i => i.platform === 'web');

  if (mobile.length > 0 && web.length > 0) {
    const count = Math.max(mobile.length, web.length);
    return Array.from({ length: count }, (_, i) => ({
      mobile: mobile[i] || null,
      web: web[i] || null,
    }));
  }

  return null;
};

const ImageCell = ({ image, delay = 0 }) => (
  <CompareCell
    $mobile={image.platform === 'mobile'}
    $web={image.platform === 'web'}
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
    viewport={{ once: true, margin: '-40px' }}
  >
    <PlatformLabel $mobile={image.platform === 'mobile'}>
      {image.platform === 'mobile' ? <FaMobileAlt size={11} /> : <FaDesktop size={11} />}
      {image.label}
    </PlatformLabel>
    <CompareMedia $mobile={image.platform === 'mobile'} $web={image.platform === 'web'}>
      <ImageDisplay image={image} compact />
    </CompareMedia>
  </CompareCell>
);

const ComparePair = ({ pair, pairIndex }) => {
  const baseDelay = pairIndex * 0.1;

  return (
    <CompareRow>
      {pair.mobile && (
        <>
          <CompareLabelSlot
            $mobile
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: baseDelay }}
            viewport={{ once: true, margin: '-40px' }}
          >
            <PlatformLabel $mobile $compact>
              <FaMobileAlt size={11} />
              {pair.mobile.label}
            </PlatformLabel>
          </CompareLabelSlot>
          <CompareMediaSlot
            $mobile
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: baseDelay }}
            viewport={{ once: true, margin: '-40px' }}
          >
            <ImageDisplay image={pair.mobile} />
          </CompareMediaSlot>
        </>
      )}
      {pair.web && (
        <>
          <CompareLabelSlot
            $web
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: baseDelay + 0.06 }}
            viewport={{ once: true, margin: '-40px' }}
          >
            <PlatformLabel $compact>
              <FaDesktop size={11} />
              {pair.web.label}
            </PlatformLabel>
          </CompareLabelSlot>
          <CompareMediaSlot
            $web
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: baseDelay + 0.08 }}
            viewport={{ once: true, margin: '-40px' }}
          >
            <ImageDisplay image={pair.web} />
          </CompareMediaSlot>
        </>
      )}
    </CompareRow>
  );
};

const SectionImages = ({ section }) => {
  const pairs = getComparePairs(section.images);
  const webOnly = section.images.every(i => i.platform === 'web');

  if (pairs) {
    return (
      <ImageGrid>
        {pairs.map((pair, pairIndex) => (
          <ComparePair
            key={`${section.id}-pair-${pairIndex}`}
            pair={pair}
            pairIndex={pairIndex}
          />
        ))}
      </ImageGrid>
    );
  }

  if (webOnly) {
    return (
      <WebOnlyGrid $count={section.images.length}>
        {section.images.map((image, i) => (
          <ImageCell key={`${section.id}-web-${i}`} image={image} delay={i * 0.1} />
        ))}
      </WebOnlyGrid>
    );
  }

  return (
    <SingleImageRow>
      {section.images.map((image, i) => (
        <ImageCell key={`${section.id}-single-${i}`} image={image} delay={i * 0.1} />
      ))}
    </SingleImageRow>
  );
};

const ImageDisplay = ({ image, compact = false }) => {
  const openLightbox = useContext(LightboxContext);

  const handleOpen = () => {
    openLightbox?.({ src: image.src, alt: image.label });
  };

  if (image.platform === 'mobile') {
    return (
      <MobilePhoneSmall
        $compact={compact}
        $clickable={!!openLightbox}
        onClick={handleOpen}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => event.key === 'Enter' && handleOpen()}
      >
        <img src={image.src} alt={image.label} />
      </MobilePhoneSmall>
    );
  }

  return (
    <BrowserFrame
      $fillHeight={!compact}
      $clickable={!!openLightbox}
      onClick={handleOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => event.key === 'Enter' && handleOpen()}
    >
      <div className="browser-bar">
        <span className="dot red" />
        <span className="dot yellow" />
        <span className="dot green" />
        <span className="url-bar">altinmobileweb.com</span>
      </div>
      <img src={image.src} alt={image.label} />
    </BrowserFrame>
  );
};

const filterSection = (section, activeFilter) => {
  if (activeFilter === 'all') return true;
  if (activeFilter === 'mobile') return section.filter === 'mobile' || section.filter === 'all';
  if (activeFilter === 'web') return section.filter === 'web' || section.filter === 'all';
  return true;
};

const filterImages = (images, activeFilter) => {
  if (activeFilter === 'all') return images;
  return images.filter(img => img.platform === activeFilter);
};

const PhoneInner = styled(HeroLayer)``;
const PlatformInner = styled(HeroLayer)``;

const useSmoothIntroProgress = (introRef) => {
  const autoProgress = useMotionValue(0);
  const { scrollYProgress } = useScroll({
    target: introRef,
    offset: ['start start', 'end start'],
  });

  const combined = useTransform([autoProgress, scrollYProgress], ([auto, scroll]) =>
    Math.min(1, Math.max(auto, 0.26 + scroll * 0.68))
  );

  const smooth = useSpring(combined, {
    stiffness: 48,
    damping: 30,
    mass: 0.88,
    restDelta: 0.0001,
  });

  useEffect(() => {
    const controls = animate(autoProgress, 0.26, {
      duration: 3.6,
      ease: [0.22, 1, 0.36, 1],
    });
    return () => controls.stop();
  }, [autoProgress]);

  return smooth;
};

const scrollIntroStep = () => {
  window.scrollBy({
    top: Math.max(window.innerHeight * 0.38, 260),
    behavior: 'smooth',
  });
};

const INTRO_DRAG_CLICK_THRESHOLD = 8;

const useIntroPointerScroll = (onTap, enabled = true) => {
  const dragState = useRef({
    pointerId: null,
    startY: 0,
    lastY: 0,
    moved: false,
  });

  const clearDrag = () => {
    dragState.current.pointerId = null;
  };

  const onPointerDown = (event) => {
    if (!enabled || event.pointerType === 'touch') return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    dragState.current = {
      pointerId: event.pointerId,
      startY: event.clientY,
      lastY: event.clientY,
      moved: false,
    };

    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event) => {
    if (!enabled || event.pointerType === 'touch') return;
    if (dragState.current.pointerId !== event.pointerId) return;

    const deltaY = event.clientY - dragState.current.lastY;
    dragState.current.lastY = event.clientY;

    if (Math.abs(event.clientY - dragState.current.startY) >= INTRO_DRAG_CLICK_THRESHOLD) {
      dragState.current.moved = true;
    }

    if (deltaY !== 0) {
      window.scrollBy({ top: deltaY, left: 0, behavior: 'auto' });
    }
  };

  const onPointerUp = (event) => {
    if (dragState.current.pointerId !== event.pointerId) return;

    const wasMoved = dragState.current.moved;
    clearDrag();

    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // pointer already released
    }

    if (!wasMoved) {
      onTap();
    }
  };

  const onPointerCancel = (event) => {
    if (dragState.current.pointerId === event.pointerId) {
      clearDrag();
    }
  };

  return { onPointerDown, onPointerMove, onPointerUp, onPointerCancel };
};

const CinematicIntro = ({ introRef, smoothProgress, children }) => {
  const [enableIntroDrag, setEnableIntroDrag] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(pointer: fine)').matches
  );

  useEffect(() => {
    const media = window.matchMedia('(pointer: fine)');
    const update = () => setEnableIntroDrag(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  const introPointerScroll = useIntroPointerScroll(scrollIntroStep, enableIntroDrag);
  const phoneScale = useTransform(smoothProgress, [0, 0.26, 0.36, 0.50], [0.72, 1, 1, 0.94]);
  const phoneZ = useTransform(smoothProgress, [0, 0.26, 0.36, 0.50], [-120, 0, 0, -40]);
  const phoneOpacity = useTransform(smoothProgress, [0, 0.1, 0.34, 0.50], [0, 1, 1, 0]);

  const platformOpacity = useTransform(smoothProgress, [0.34, 0.46, 0.58, 0.72], [0, 1, 1, 0]);
  const platformScale = useTransform(smoothProgress, [0.32, 0.46, 0.72], [0.94, 1, 1.02]);
  const platformY = useTransform(smoothProgress, [0.32, 0.46], [22, 0]);

  const bridgeOpacity = useTransform(smoothProgress, [0.54, 0.74], [0, 1]);

  const hintPhase1 = useTransform(smoothProgress, [0.12, 0.2, 0.32, 0.40], [0, 1, 1, 0]);
  const hintPhase2 = useTransform(smoothProgress, [0.44, 0.52, 0.64, 0.72], [0, 1, 1, 0]);
  const hintOpacity = useTransform([hintPhase1, hintPhase2], ([a, b]) => Math.max(a, b));

  const contentOpacity = useTransform(smoothProgress, [0.58, 0.78], [0, 1]);
  const contentY = useTransform(smoothProgress, [0.58, 0.80], [30, 0]);

  return (
    <>
      <IntroSection ref={introRef}>
        <IntroSticky
          role={enableIntroDrag ? 'button' : undefined}
          tabIndex={enableIntroDrag ? 0 : undefined}
          aria-label="Keşfetmek için kaydır"
          {...(enableIntroDrag ? introPointerScroll : {})}
          onKeyDown={enableIntroDrag ? (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              scrollIntroStep();
            }
          } : undefined}
        >
          <IntroBg />

          <HeroStage>
            <PhoneInner
              style={{
                scale: phoneScale,
                z: phoneZ,
                opacity: phoneOpacity,
                transformOrigin: '50% 50%',
              }}
            >
              <HeroPhoneImg
                src={girisGif}
                alt="Altın Mobile Giriş"
                draggable={false}
              />
            </PhoneInner>

            <PlatformInner
              style={{
                opacity: platformOpacity,
                y: platformY,
                scale: platformScale,
              }}
            >
              <HeroPlatformImg
                src={tumPlatformlarda}
                alt="Tüm platformlarda"
                draggable={false}
              />
            </PlatformInner>
          </HeroStage>

          <IntroFadeBridge style={{ opacity: bridgeOpacity }} />

          <ScrollHint style={{ opacity: hintOpacity }}>
            Keşfetmek için kaydır
            <FaChevronDown size={16} />
          </ScrollHint>
        </IntroSticky>
      </IntroSection>

      <ContentArea style={{ opacity: contentOpacity, y: contentY }}>
        {children}
      </ContentArea>
    </>
  );
};

const ScrollNavigation = () => {
  const [showTop, setShowTop] = useState(false);
  const [showBottom, setShowBottom] = useState(true);

  useEffect(() => {
    const update = () => {
      const y = window.scrollY;
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight - 8;
      setShowTop(y > 320);
      setShowBottom(y < maxScroll - 320);
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToBottom = () => {
    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior: 'smooth',
    });
  };

  return (
    <ScrollFabGroup>
      <AnimatePresence>
        {showTop && (
          <ScrollFab
            key="scroll-top"
            type="button"
            aria-label="En üste dön"
            onClick={scrollToTop}
            initial={{ opacity: 0, y: 12, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.9 }}
            whileTap={{ scale: 0.94 }}
          >
            <FaChevronUp size={16} />
          </ScrollFab>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showBottom && (
          <ScrollFab
            key="scroll-bottom"
            type="button"
            aria-label="En alta in"
            onClick={scrollToBottom}
            initial={{ opacity: 0, y: 12, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.9 }}
            whileTap={{ scale: 0.94 }}
          >
            <FaChevronDown size={16} />
          </ScrollFab>
        )}
      </AnimatePresence>
    </ScrollFabGroup>
  );
};

export const Explore2 = () => {
  const [activePanel, setActivePanel] = useState('all');
  const [lightboxImage, setLightboxImage] = useState(null);
  const introRef = useRef(null);
  const panelRef = useRef(null);
  const smoothProgress = useSmoothIntroProgress(introRef);

  const handlePanelChange = (panelId) => {
    setActivePanel(panelId);

    if (PANEL_SCROLL_IDS.has(panelId)) {
      window.setTimeout(() => {
        panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 120);
    }
  };

  const visibleSections = sections
    .filter(section => filterSection(section, activePanel))
    .map(section => ({
      ...section,
      images: filterImages(section.images, activePanel),
    }))
    .filter(section => section.images.length > 0);

  const showGallery = GALLERY_PANELS.has(activePanel);

  return (
    <LightboxContext.Provider value={setLightboxImage}>
      <PageWrapper>
        <ImageLightbox image={lightboxImage} onClose={() => setLightboxImage(null)} />
        <ScrollNavigation />

        <CinematicIntro introRef={introRef} smoothProgress={smoothProgress}>
        <StickyHeader>
          <HeaderInner>
            <HeaderTitle>Altın Mobile</HeaderTitle>
            <FilterTabs>
              {filters.map((filter) => {
                const Icon = filter.icon;
                return (
                  <FilterTab
                    key={filter.id}
                    $active={activePanel === filter.id}
                    onClick={() => handlePanelChange(filter.id)}
                    whileTap={{ scale: 0.96 }}
                  >
                    <Icon size={13} />
                    <span className="label-full">{filter.label}</span>
                    <span className="label-short">{filter.shortLabel}</span>
                  </FilterTab>
                );
              })}
            </FilterTabs>
          </HeaderInner>
        </StickyHeader>

        <ContentSection ref={panelRef}>
          <AnimatePresence mode="wait">
            {showGallery && (
              <motion.div
                key="gallery"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.35 }}
              >
                {visibleSections.map((section, sectionIndex) => (
                  <ShowcaseCard
                    key={section.id}
                    layout
                    initial={{ opacity: 0, y: 60 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: sectionIndex * 0.05 }}
                    viewport={{ once: true, margin: '-80px' }}
                  >
                    <CardHeader>
                      <CardTag>{section.tag}</CardTag>
                      <CardTitle>{section.title}</CardTitle>
                      <CardDescription>{section.description}</CardDescription>
                    </CardHeader>

                    <SectionImages section={section} />
                  </ShowcaseCard>
                ))}
              </motion.div>
            )}

            {activePanel === 'download' && (
              <PanelSection
                key="download"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.35 }}
              >
                <Download embedded />
              </PanelSection>
            )}

            {activePanel === 'pricing' && (
              <PanelSection
                key="pricing"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.35 }}
              >
                <PriceCalculator embedded />
              </PanelSection>
            )}

            {activePanel === 'contact' && (
              <PanelSection
                key="contact"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.35 }}
              >
                <ContactUs />
              </PanelSection>
            )}
          </AnimatePresence>
        </ContentSection>
      </CinematicIntro>
      </PageWrapper>
    </LightboxContext.Provider>
  );
};
